import { type Database } from "@fitness-app/supabase";
import { z } from "zod";

import { type TraineeStatus } from "./Trainee";

export type TeamMemberEntity = Database["public"]["Tables"]["team_member"]["Row"];
export type TeamMemberAssignedEntity = Database["public"]["Tables"]["team_member_assigned"]["Row"];

export const teamMemberSettingsSchema = z.object({
  nutritionGenerator: z.boolean().optional(),
  allowAiAgent: z.boolean().optional(),
});

export type TeamMemberSettings = z.infer<typeof teamMemberSettingsSchema>;

export const parseTeamMemberSettings = (value: unknown): TeamMemberSettings => {
  const result = teamMemberSettingsSchema.safeParse(value);
  return result.success ? result.data : {};
};

export type TeamMemberChatAgentMode = "automatic" | "manual";
export type TeamMemberChatAgentReplyMode = "automatic" | "suggestion";
export type TeamMemberChatAgentSensitivity = "low" | "balanced" | "high";

export type TeamMemberChatAgentConfig = {
  enabled: boolean;
  mode: TeamMemberChatAgentMode;
  replyMode: TeamMemberChatAgentReplyMode;
  sensitivity: TeamMemberChatAgentSensitivity;
  tone: string | null;
  writingStyle: string | null;
  additionalInstructions: string | null;
};

type TeamMemberChatAgentConfigInput = {
  enabled?: unknown;
  mode?: unknown;
  replyMode?: unknown;
  sensitivity?: unknown;
  tone?: unknown;
  writingStyle?: unknown;
  additionalInstructions?: unknown;
};

const isTeamMemberChatAgentConfigObject = (value: unknown): value is TeamMemberChatAgentConfigInput =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const isTeamMemberChatAgentReplyMode = (value: unknown): value is TeamMemberChatAgentReplyMode =>
  value === "automatic" || value === "suggestion";

const normalizeTeamMemberChatAgentMode = ({
  rawMode,
  rawReplyMode,
}: {
  rawMode: unknown;
  rawReplyMode: unknown;
}): TeamMemberChatAgentMode => {
  if (isTeamMemberChatAgentReplyMode(rawReplyMode)) {
    return rawMode === "manual" ? "manual" : "automatic";
  }

  if (rawMode === "manual") {
    return "manual";
  }

  // Legacy configs stored reply behavior in `mode`, so keep them effectively manual.
  if (isTeamMemberChatAgentReplyMode(rawMode)) {
    return "manual";
  }

  return "automatic";
};

const normalizeTeamMemberChatAgentReplyMode = ({
  rawMode,
  rawReplyMode,
}: {
  rawMode: unknown;
  rawReplyMode: unknown;
}): TeamMemberChatAgentReplyMode => {
  if (isTeamMemberChatAgentReplyMode(rawReplyMode)) {
    return rawReplyMode;
  }

  if (isTeamMemberChatAgentReplyMode(rawMode)) {
    return rawMode;
  }

  return "suggestion";
};

const normalizeNullableString = (value: unknown): string | null => (typeof value === "string" ? value : null);

const isTeamMemberChatAgentSensitivity = (value: unknown): value is TeamMemberChatAgentSensitivity =>
  value === "low" || value === "balanced" || value === "high";

const normalizeTeamMemberChatAgentSensitivity = (value: unknown): TeamMemberChatAgentSensitivity =>
  isTeamMemberChatAgentSensitivity(value) ? value : "balanced";

export const normalizeTeamMemberChatAgentConfig = (config: unknown): TeamMemberChatAgentConfig | null => {
  if (!isTeamMemberChatAgentConfigObject(config)) {
    return null;
  }

  return {
    enabled: Boolean(config.enabled),
    mode: normalizeTeamMemberChatAgentMode({
      rawMode: config.mode,
      rawReplyMode: config.replyMode,
    }),
    replyMode: normalizeTeamMemberChatAgentReplyMode({
      rawMode: config.mode,
      rawReplyMode: config.replyMode,
    }),
    sensitivity: normalizeTeamMemberChatAgentSensitivity(config.sensitivity),
    tone: normalizeNullableString(config.tone),
    writingStyle: normalizeNullableString(config.writingStyle),
    additionalInstructions: normalizeNullableString(config.additionalInstructions),
  };
};

export type SalesCommissionConfig = {
  // Percentages as plain numbers (5 = 5%), amounts in grosze.
  installmentsPercent?: number;
  oneTimePercent?: number;
  baseAmount?: number;
  // Low-ticket "Bikini" rules: subscription pays a percent of the first
  // payment, one-time bikini pays a flat amount per sale.
  bikiniSubscriptionPercent?: number;
  bikiniFlatAmount?: number;
};

export type TeamMember = Omit<
  TeamMemberEntity,
  "specializationTags" | "variables" | "settings" | "chatAgentConfig" | "salesCommissionConfig"
> & {
  salesCommissionConfig?: SalesCommissionConfig | null;
  specializationTags: string[] | null;
  variables: Record<string, string | number> | null;
  settings: TeamMemberSettings;
  chatAgentConfig: TeamMemberChatAgentConfig | null;
};

export type TeamMemberRoleEnum = Database["public"]["Enums"]["TeamMemberRole"];
export const TeamMemberRole: Record<
  Database["public"]["Enums"]["TeamMemberRole"],
  Database["public"]["Enums"]["TeamMemberRole"]
> = {
  SHARED_TRAINER: "SHARED_TRAINER",
  REGULAR_TRAINER: "REGULAR_TRAINER",
  ACCOUNT_OWNER: "ACCOUNT_OWNER",
  ADMINISTRATION: "ADMINISTRATION",
  SALES_REP: "SALES_REP",
  SUPPORT: "SUPPORT",
} as const;

export type TeamMemberStatusEnum = Database["public"]["Enums"]["TeamMemberStatus"];
export const TeamMemberStatus: Record<
  Database["public"]["Enums"]["TeamMemberStatus"],
  Database["public"]["Enums"]["TeamMemberStatus"]
> = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
  PENDING: "PENDING",
  INVITED: "INVITED",
  // Odpowiednik usunięcia: wiersz i profil zostają (autorstwo przepisów, planów
  // i wiadomości się trzyma), a dostęp odcina ban na koncie w auth. INACTIVE to
  // pauza, z której się wraca — DELETED nie (TRE-1731).
  DELETED: "DELETED",
} as const;

export type AssignedTrainer = {
  createdAt?: string;
  trainer: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    status: TeamMemberStatusEnum;
    user: { avatarUrl: string | null; id: string } | null;
  };
};

export type AssignedTrainerWithId = AssignedTrainer & {
  assignedTrainerId: string;
};

export type TeamMemberWithUser = TeamMember & {
  user: {
    firstName: string;
    lastName: string;
    avatarUrl: string;
  } | null;
  assignedTrainees: { id: string }[];
  assignedTrainers: AssignedTrainer[];
};

export type TeamMemberWithTrainees = TeamMember & {
  user: {
    firstName: string;
    lastName: string;
    avatarUrl: string;
    id: string;
  } | null;
  assignedTrainees: {
    id: string;
    firstName: string;
    lastName: string;
    avatarUrl: string;
    email: string;
    status: TraineeStatus;
    tags?: string[];
    ghost?: boolean;
  }[];
};
